import { getRecordFormat, type MoodCheckin, type ThoughtRecord, type VoiceLog } from '@adhd/shared';
import { Link } from 'react-router-dom';
import { CheckinCard } from './CheckinCard.js';
import { formatTime, whenWritten, writtenLate } from '../lib/mood.js';

/**
 * One day's record, drawn the same way wherever it appears.
 *
 * Today and the Log had grown into near-inversions of each other: Today listed
 * check-ins and hid every entry behind a count, the Log listed entries and
 * folded the check-ins away. Same day, opposite screens. This file is the half
 * they now share — the row shapes and the rule about what nests under what —
 * so the two can differ in layout, which is the point, without differing about
 * what a journal entry looks like.
 *
 * The rule: a count sits with the thing it counts, and a thing is drawn once.
 * Whole if it fits, as a row that opens it if it does not, and on its parent's
 * elbow if it came from something else.
 */

export type FeedItem =
  | { kind: 'checkin'; at: string; data: MoodCheckin }
  | { kind: 'journal'; at: string; data: VoiceLog }
  | { kind: 'record'; at: string; data: ThoughtRecord };

export function truncate(s: string, n: number): string {
  return s.length > n ? `${s.slice(0, n - 1)}…` : s;
}

/**
 * What an entry is called in a list.
 *
 * `||` at every link, not `??`. Three of his entries carry `title = ''` — a
 * present-but-empty title, which `??` does not fall through — so the row
 * rendered as an empty bold tag while holding a good summary and a
 * 343-character transcript underneath it.
 *
 * The last link is not model-written, so a row is never blank at any AI
 * setting: below `summarise` there is no title and no summary, and a typed
 * entry still has its own text.
 */
export function entryTitle(e: VoiceLog): string {
  return (
    e.title?.trim() ||
    truncate(e.summary?.trim() || e.transcript?.trim() || 'Journal entry', 70)
  );
}

/**
 * The line under an entry's name: the app's own facts about it.
 *
 * The mood he gave the entry joins this line rather than floating beside it as
 * a card of its own. That card was the thing he reported — a check-in saved
 * from inside an entry has no tags and no note by construction, so it arrived
 * on Today as an empty box with the meaning stripped off, next to nothing that
 * said where it came from.
 *
 * It goes after the app's facts and never in the name slot: the name is what he
 * wrote about, and a number should never outrank his own sentence.
 */
function entryMeta(e: VoiceLog): JSX.Element {
  return (
    <span>
      {/* The day it belongs to already labels the section above it, so what is
          missing is WHEN it was written — and the clock time alone, under a
          Saturday heading, reads as Saturday. */}
      Journal · {writtenLate(e.day, e.recordedAt) ? whenWritten(e.recordedAt, undefined, e.day) : formatTime(e.recordedAt)}
      {e.durationSeconds ? ` · ${Math.round(e.durationSeconds)}s` : ''}
      {e.transcriptStatus === 'pending' || e.transcriptStatus === 'processing'
        ? ' · writing it up…'
        : ''}
      {e.transcriptStatus === 'failed' ? " · couldn't transcribe" : ''}
      {/*
        'skipped' is what AI-off produces, and it had no branch at all — so a
        spoken entry read "Journal · 4:18 PM · 58s" and looked like a row that
        had lost something. Nothing was lost: the recording is there and nobody
        was allowed to listen to it. Said plainly, so the row is not a small
        mystery every time he scrolls past it.
      */}
      {e.transcriptStatus === 'skipped' ? ' · kept as a recording' : ''}

    </span>
  );
}

/**
 * A journal entry, with whatever came out of it hanging underneath.
 *
 * The thought record keeps the rail: it is a one-line `.option`, which is the
 * shape `.linked-child` was tuned for (`bottom: 50%`, `min-height: 56px`). A
 * check-in card is not — with tags and a note the elbow would terminate in the
 * middle of a 140px block, pointing at the note text — which is why the mood
 * joins the line above instead of hanging off it.
 */
export function EntryRow({
  e,
  record,
  mood,
  quiet = false,
  today,
  onCheckinRemoved,
}: {
  e: VoiceLog;
  record?: ThoughtRecord;
  mood?: MoodCheckin;
  /** Today. Drops progress state — see RecordRow. */
  quiet?: boolean;
  /** Today where the reader is; enables the bin on the nested mood. */
  today?: string;
  onCheckinRemoved?: () => void;
}): JSX.Element {
  return (
    <div className="linked-pair">
      <Link className="option" to={`/journal/${e.id}`}>
        <strong>{entryTitle(e)}</strong>
        {entryMeta(e)}
      </Link>
      {/*
        The mood he gave this entry, on the same elbow the record uses.
        
        It was absorbed into the line above as "· Good", which is tighter and
        which he did not want: he had been shown this drawn on the rail and
        asked where it went. A middot says two facts about one row; the rail says
        one thing came out of another, which is what actually happened.
        
        The objection to this was geometry — the rail is `bottom: 50%` with
        `min-height: 56px`, tuned for a one-line row, and a check-in card with
        tags and a note would leave the elbow pointing at its middle. It does not
        apply here: a mood saved from an entry is written with no tags and no
        note by construction, so the card is exactly the two-line shape the rail
        was built for. Measured before shipping.
      */}
      {mood && (
        <div className="linked-child">
          <CheckinCard c={mood} today={today} onGone={onCheckinRemoved} label="How you felt about this" />
        </div>
      )}
      {record && (
        <Link className="option linked-child" to={`/thought-record/${record.id}`}>
          <strong>{truncate(record.automaticThought, 60)}</strong>
          <span>
            Thought record from this
            {!quiet && !record.isComplete && ' · unfinished'}
          </span>
        </Link>
      )}
    </div>
  );
}

/**
 * A thought record started on its own, rather than out of an entry.
 *
 * `quiet` drops the progress state, and Today passes it. In the Log
 * "· unfinished" sits under a heading called "Worth finishing", which frames it
 * as an invitation. On the screen he opens first on a bad morning, a list of
 * things he started and abandoned is a list of what is missing — and unfinished
 * records are exactly what accumulates during a bad stretch, so the harm scales
 * with the state the screen is most often in.
 */
export function RecordRow({ r, quiet = false }: { r: ThoughtRecord; quiet?: boolean }): JSX.Element {
  return (
    <Link className="option" to={`/thought-record/${r.id}`}>
      <strong>{truncate(r.automaticThought, 70)}</strong>
      <span>
        {r.format === 'standard' ? 'Thought record' : getRecordFormat(r.format).name} ·{' '}
        {formatTime(r.occurredAt)}
        {!quiet && r.beliefShift !== null && r.beliefShift > 0 && (
          <> · belief dropped {r.beliefShift} points</>
        )}
        {!quiet && !r.isComplete && <> · unfinished</>}
      </span>
    </Link>
  );
}

/**
 * The day, newest first, in one list.
 *
 * A check-in that came out of an entry in this same list is not drawn: it is
 * already on that entry's line. One thing, drawn once. A check-in whose parent
 * is not here — an entry from another day — keeps its own card, because the
 * alternative is silently dropping something he recorded.
 */
export function DayFeed({
  items,
  today,
  onCheckinRemoved,
  records,
  quiet = false,
}: {
  items: FeedItem[];
  /** Today where the reader is; enables the bin on the cards it may appear on. */
  today?: string;
  onCheckinRemoved?: () => void;
  /** Thought records by the id of the entry they were drafted from. */
  records: Map<string, ThoughtRecord>;
  /** Today. Drops progress state from record rows. */
  quiet?: boolean;
}): JSX.Element {
  const entryIds = new Set(items.filter((i) => i.kind === 'journal').map((i) => i.data.id));
  const moodFor = new Map<string, MoodCheckin>();
  for (const i of items) {
    if (i.kind === 'checkin' && i.data.voiceLogId && entryIds.has(i.data.voiceLogId)) {
      moodFor.set(i.data.voiceLogId, i.data);
    }
  }
  // A record shown under its parent is not also shown on its own.
  const nested = new Set([...records.values()].map((r) => r.id));

  return (
    <div className="stack-sm stack">
      {items.map((i) =>
        i.kind === 'journal' ? (
          <EntryRow
            key={i.data.id}
            e={i.data}
            record={records.get(i.data.id)}
            mood={moodFor.get(i.data.id)}
            quiet={quiet}
            today={today}
            onCheckinRemoved={onCheckinRemoved}
          />
        ) : i.kind === 'record' ? (
          nested.has(i.data.id) ? null : <RecordRow key={i.data.id} r={i.data} quiet={quiet} />
        ) : i.data.voiceLogId && entryIds.has(i.data.voiceLogId) ? null : (
          <CheckinCard key={i.data.id} c={i.data} today={today} onGone={onCheckinRemoved} />
        ),
      )}
    </div>
  );
}
